import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Stack,
  Tabs,
  Tab,
  Skeleton,
} from "@mui/material";
import { Download, DateRange, RestartAlt } from "@mui/icons-material";
import { Pie, Bar, Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import api from "../../utils/api";
import ArchitecturalRequestsExportPage from "./ArchitecturalRequestsExportPage";
import { processArchitecturalComparison } from "./utils/processArchitecturalComparison";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const softColors = [
  "#A3E4D7",
  "#F9E79F",
  "#AED6F1",
  "#F5B7B1",
  "#D2B4DE",
  "#A9CCE3",
  "#FAD7A0",
  "#A2D9CE",
  "#F5CBA7",
  "#BB8FCE",
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function offsetDays(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function firstOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function addDaysISO(dateStr, days) {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const presets = [
  {
    key: "30d",
    label: "Last 30 days",
    get: () => ({ start: offsetDays(30), end: todayISO() }),
  },
  {
    key: "90d",
    label: "Last 90 days",
    get: () => ({ start: offsetDays(90), end: todayISO() }),
  },
  {
    key: "mtd",
    label: "MTD",
    get: () => ({ start: firstOfMonthISO(), end: todayISO() }),
  },
  {
    key: "ytd",
    label: "YTD",
    get: () => ({
      start: `${new Date().getFullYear()}-01-01`,
      end: todayISO(),
    }),
  },
];

const StatTile = ({ label, value }) => (
  <Card
    elevation={0}
    sx={{
      ...adminCardSx,
      textAlign: "center",
      p: 3,
      height: "100%",
    }}
  >
    <Typography
      variant="overline"
      sx={{
        letterSpacing: 1,
        color: "#6b778c",
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h3"
      sx={{
        fontWeight: 800,
        color: "#223142",
        lineHeight: 1.1,
        mt: 0.75,
      }}
    >
      {value}
    </Typography>
  </Card>
);

const SectionHeader = ({ title, subtitle, right }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      gap: 1.5,
      mb: 2,
    }}
  >
    <Box>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "#223142", mb: subtitle ? 0.5 : 0 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {right}
  </Box>
);

const EmptyState = () => (
  <Card
    elevation={0}
    sx={{
      ...adminCardSx,
      textAlign: "center",
      py: 6,
      px: 3,
    }}
  >
    <Typography variant="h6" sx={{ mb: 1, color: "#223142", fontWeight: 700 }}>
      No data to display
    </Typography>
    <Typography variant="body2" sx={{ color: "#6b778c" }}>
      Try broadening your filters or selecting a different date range.
    </Typography>
  </Card>
);

const ArchitecturalRequestsTab = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [trendView, setTrendView] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");
  const overviewRef = useRef(null);

  useEffect(() => {
    const { start, end } = presets.find((p) => p.key === "90d").get();
    setStartDate(start);
    setEndDate(end);
    fetchFilters();
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;
    const t = setTimeout(() => fetchData(), 400);
    return () => clearTimeout(t);
  }, [startDate, endDate, selectedType, selectedSubtype]);

  const fetchFilters = async () => {
    try {
      const [typesRes, subtypesRes] = await Promise.all([
        api.get("/architectural-request-types"),
        api.get("/architectural-request-subtypes"),
      ]);
      setTypes(typesRes.data || []);
      setSubtypes(subtypesRes.data || []);
    } catch (err) {
      console.error("Error loading filters:", err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", addDaysISO(endDate, 1));
      if (selectedType) params.append("type_id", selectedType);
      if (selectedSubtype) params.append("subtype_id", selectedSubtype);
      params.append("includeMember", "1");

      const url = `/getArchitecturalRequestsReport?${params.toString()}`;
      const { data } = await api.get(url);
      setRequests(data || []);
    } catch (err) {
      console.error("Fetch error in ArchitecturalRequestsReport:", err);
    } finally {
      setLoading(false);
    }
  };

  const topSchemes = useMemo(() => {
    const counts = {};
    for (const r of requests) {
      if (r.scheme_name) {
        counts[r.scheme_name] = (counts[r.scheme_name] || 0) + 1;
      }
      if (Array.isArray(r.color_selections)) {
        for (const c of r.color_selections) {
          if (c.scheme_name) {
            counts[c.scheme_name] = (counts[c.scheme_name] || 0) + 1;
          }
        }
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [requests]);

  const topColors = useMemo(() => {
    const counts = {};
    for (const r of requests) {
      if (r.option_color_name) {
        counts[r.option_color_name] = (counts[r.option_color_name] || 0) + 1;
      }
      if (Array.isArray(r.color_selections)) {
        for (const c of r.color_selections) {
          if (c.option_color_name) {
            counts[c.option_color_name] = (counts[c.option_color_name] || 0) + 1;
          }
        }
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [requests]);

  const buildTopBarChart = (data, label) => ({
    labels: data.map(([name]) => name),
    datasets: [
      {
        label,
        data: data.map(([, count]) => count),
        backgroundColor: "rgba(47,99,173,0.58)",
        borderColor: "rgba(47,99,173,0.88)",
        borderWidth: 1,
        borderRadius: 8,
      },
    ],
  });

  const handleResetFilters = () => {
    setSelectedType("");
    setSelectedSubtype("");
    const { start, end } = presets.find((p) => p.key === "90d").get();
    setStartDate(start);
    setEndDate(end);
  };

  const total = requests.length;
  const approved = requests.filter(
    (r) => r.status?.toLowerCase() === "approved"
  ).length;
  const completed = requests.filter(
    (r) => r.status?.toLowerCase() === "completed"
  ).length;
  const open = total - approved - completed;

  const comparisonData = useMemo(
    () => processArchitecturalComparison(requests, "requested_at", trendView),
    [requests, trendView]
  );

  const trendChartData = {
    labels: comparisonData.labels,
    datasets: [
      {
        label: `This ${trendView}`,
        data: comparisonData.current,
        borderColor: "rgba(47,99,173,0.85)",
        backgroundColor: "rgba(47,99,173,0.16)",
        fill: true,
        tension: 0.35,
        pointRadius: 3,
      },
      {
        label: `Last ${trendView}`,
        data: comparisonData.previous,
        borderColor: "rgba(220,38,38,0.82)",
        backgroundColor: "rgba(220,38,38,0.12)",
        fill: true,
        tension: 0.35,
        pointRadius: 3,
      },
    ],
  };

  const verticalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#223142",
        backgroundColor: "rgba(255,255,255,0.92)",
        borderColor: "rgba(0,0,0,0.08)",
        borderWidth: 1,
        borderRadius: 4,
        font: { weight: "bold", size: 11 },
        padding: 4,
        anchor: "end",
        align: "top",
        formatter: (value) => value,
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.raw}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(47,99,173,0.08)" },
        ticks: { font: { size: 12 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(47,99,173,0.08)" },
        ticks: { font: { size: 12 } },
      },
    },
  };

  const horizontalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    layout: {
      padding: { left: 12, right: 32, top: 8, bottom: 8 },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#223142",
        backgroundColor: "rgba(255,255,255,0.92)",
        borderColor: "rgba(0,0,0,0.08)",
        borderWidth: 1,
        borderRadius: 4,
        font: { weight: "bold", size: 11 },
        padding: 4,
        anchor: "end",
        align: "right",
        offset: 8,
        clamp: true,
        clip: false,
        formatter: (value) => value,
        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.8)",
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 8,
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.raw}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: "rgba(47,99,173,0.08)" },
        ticks: {
          font: { size: 11 },
          stepSize: 1,
          precision: 0,
        },
      },
      y: {
        grid: { display: false },
        ticks: {
          font: { size: 11 },
          autoSkip: false,
          maxTicksLimit: 10,
          callback: function (val) {
            const label = this.getLabelForValue(val);
            return label.length > 25 ? `${label.slice(0, 25)}…` : label;
          },
        },
      },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      datalabels: { display: false },
    },
    scales: {
      x: { grid: { color: "rgba(47,99,173,0.06)" } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(47,99,173,0.08)" },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 12, padding: 10, font: { size: 12 } },
      },
      datalabels: {
        color: "#111",
        font: { weight: "bold", size: 11 },
        backgroundColor: "rgba(255,255,255,0.92)",
        borderColor: "rgba(0,0,0,0.08)",
        borderWidth: 1,
        borderRadius: 6,
        padding: 5,
        formatter: (value, ctx) => {
          const data = ctx.chart.data.datasets[0].data || [];
          const total = data.reduce((a, b) => a + b, 0);
          const pct = total ? Math.round((value / total) * 100) : 0;
          return `${pct}%`;
        },
      },
    },
  };

  const typeCounts = useMemo(() => {
    const acc = {};
    for (const r of requests) {
      const name = r.request_type_name || "Unknown";
      acc[name] = (acc[name] || 0) + 1;
    }
    return acc;
  }, [requests]);

  const subtypeCounts = useMemo(() => {
    const acc = {};
    for (const r of requests) {
      const name = r.subtype_name || "Unknown";
      acc[name] = (acc[name] || 0) + 1;
    }
    return acc;
  }, [requests]);

  const memberTypeCounts = useMemo(() => {
    const acc = {};
    for (const r of requests) {
      const code = r?.member?.ClubMembershipTypeCode || "Unknown";
      acc[code] = (acc[code] || 0) + 1;
    }
    return acc;
  }, [requests]);

  const buildBarChart = (data, label) => ({
    labels: Object.keys(data),
    datasets: [
      {
        label,
        data: Object.values(data),
        backgroundColor: "rgba(47,99,173,0.58)",
        borderColor: "rgba(47,99,173,0.88)",
        borderWidth: 1,
        borderRadius: 8,
      },
    ],
  });

  const statusCounts = requests.reduce((acc, r) => {
    const s = r.status || "Unknown";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const priorityCounts = requests.reduce((acc, r) => {
    const p = r.priority || "Unknown";
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});

  const handleExportOverviewToPDF = async () => {
    if (!overviewRef.current) return;

    const canvas = await html2canvas(overviewRef.current, {
      scale: 2,
      backgroundColor: "#fff",
    });

    const pdf = new jsPDF("p", "mm", "a4");
    const imgData = canvas.toDataURL("image/png");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    if (imgHeight <= pageHeight) {
      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
    } else {
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }
    }

    pdf.save(`ArchitecturalRequests_${todayISO()}.pdf`);
  };

  return (
    <Box sx={{ width: "100%" }}>
  

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 2.5,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
              >
                Architectural Requests Overview
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Switch between dashboard overview and export tools.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportOverviewToPDF}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Download PDF
            </Button>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Architectural Requests Tabs"
            sx={{
              minHeight: "auto",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                minHeight: 42,
                color: "#5f6b7a",
                mr: 1,
                px: 2,
              },
              "& .Mui-selected": {
                color: "#2f63ad !important",
                backgroundColor: "rgba(47,99,173,0.08)",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Export" />
          </Tabs>
        </Box>
      </Card>

      {activeTab === 0 && (
        <Box ref={overviewRef}>
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Total" value={total} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Open" value={open} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Approved" value={approved} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Completed" value={completed} />
            </Grid>
          </Grid>

          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              mb: 2.5,
            }}
          >
            <SectionHeader
              title="Filters"
              subtitle="Refine architectural request reporting by type, subtype, and date range."
            />

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedType}
                    label="Type"
                    onChange={(e) => {
                      setSelectedType(e.target.value);
                      setSelectedSubtype("");
                    }}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {types.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth disabled={!selectedType}>
                  <InputLabel>Subtype</InputLabel>
                  <Select
                    value={selectedSubtype}
                    label="Subtype"
                    onChange={(e) => setSelectedSubtype(e.target.value)}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {subtypes
                      .filter((s) => Number(s.type_id) === Number(selectedType))
                      .map((s) => (
                        <MenuItem key={s.subtype_id} value={s.subtype_id}>
                          {s.subtype_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 2.5, flexWrap: "wrap", gap: 1 }}
            >
              {presets.map((p) => {
                const { start, end } = p.get();
                const active = startDate === start && endDate === end;
                return (
                  <Chip
                    key={p.key}
                    icon={<DateRange />}
                    label={p.label}
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    onClick={() => {
                      setStartDate(start);
                      setEndDate(end);
                    }}
                    sx={{
                      borderRadius: "10px",
                      fontWeight: 600,
                    }}
                  />
                );
              })}

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAlt />}
                onClick={handleResetFilters}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Reset Filters
              </Button>
            </Stack>
          </Card>

          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              mb: 2.5,
            }}
          >
            <SectionHeader
              title="Trend Comparison"
              subtitle="Compare current request volume against the previous period."
              right={
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {["day", "week", "month", "year"].map((v) => (
                    <Chip
                      key={v}
                      label={v}
                      size="small"
                      variant={trendView === v ? "filled" : "outlined"}
                      onClick={() => setTrendView(v)}
                      sx={{
                        textTransform: "capitalize",
                        borderRadius: "10px",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Stack>
              }
            />

            <Box sx={{ height: 320 }}>
              {loading ? (
                <Skeleton variant="rounded" height={320} />
              ) : requests.length === 0 ? (
                <EmptyState />
              ) : (
                <Line data={trendChartData} options={lineOptions} />
              )}
            </Box>
          </Card>

          <Grid container spacing={2.5} sx={{ mb: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                  height: "100%",
                }}
              >
                <SectionHeader
                  title="By Request Type"
                  subtitle="Volume by architectural request type."
                />
                <Box sx={{ height: 300 }}>
                  {loading ? (
                    <Skeleton variant="rounded" height={300} />
                  ) : requests.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Bar
                      data={buildBarChart(typeCounts, "Type")}
                      options={verticalBarOptions}
                    />
                  )}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                  height: "100%",
                }}
              >
                <SectionHeader
                  title="By Subtype"
                  subtitle="Volume by architectural request subtype."
                />
                <Box sx={{ height: 300 }}>
                  {loading ? (
                    <Skeleton variant="rounded" height={300} />
                  ) : requests.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Bar
                      data={buildBarChart(subtypeCounts, "Subtype")}
                      options={verticalBarOptions}
                    />
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Card
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              mt: 2.5,
              mb: 2.5,
            }}
          >
            <SectionHeader
              title="By ClubMembershipTypeCode"
              subtitle="Grouped by member club membership type code."
            />
            <Box sx={{ height: 320 }}>
              {loading ? (
                <Skeleton variant="rounded" height={320} />
              ) : requests.length === 0 ? (
                <EmptyState />
              ) : (
                <Bar
                  data={buildBarChart(memberTypeCounts, "Membership Type")}
                  options={horizontalBarOptions}
                />
              )}
            </Box>
          </Card>

          <Grid container spacing={2.5} sx={{ mb: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                }}
              >
                <SectionHeader
                  title="By Status"
                  subtitle="Request status distribution."
                />
                <Box sx={{ height: 320, maxWidth: 520, mx: "auto" }}>
                  {loading ? (
                    <Skeleton variant="rounded" height={320} />
                  ) : requests.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Pie
                      data={{
                        labels: Object.keys(statusCounts),
                        datasets: [
                          {
                            data: Object.values(statusCounts),
                            backgroundColor: softColors,
                          },
                        ],
                      }}
                      options={pieOptions}
                    />
                  )}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                }}
              >
                <SectionHeader
                  title="By Priority"
                  subtitle="Request priority distribution."
                />
                <Box sx={{ height: 320, maxWidth: 520, mx: "auto" }}>
                  {loading ? (
                    <Skeleton variant="rounded" height={320} />
                  ) : requests.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Pie
                      data={{
                        labels: Object.keys(priorityCounts),
                        datasets: [
                          {
                            data: Object.values(priorityCounts),
                            backgroundColor: softColors,
                          },
                        ],
                      }}
                      options={pieOptions}
                    />
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                }}
              >
                <SectionHeader
                  title="Top 10 Color Schemes"
                  subtitle="Most frequently selected architectural color schemes."
                />
                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Skeleton variant="rounded" height={320} />
                  ) : topSchemes.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Bar
                      data={buildTopBarChart(topSchemes, "Top Schemes")}
                      options={horizontalBarOptions}
                    />
                  )}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  ...adminCardSx,
                  p: { xs: 2, md: 3 },
                }}
              >
                <SectionHeader
                  title="Top 10 Color Options"
                  subtitle="Most frequently selected architectural color options."
                />
                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Skeleton variant="rounded" height={320} />
                  ) : topColors.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <Bar
                      data={buildTopBarChart(topColors, "Top Colors")}
                      options={horizontalBarOptions}
                    />
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {activeTab === 1 && <ArchitecturalRequestsExportPage />}
    </Box>
  );
};

export default ArchitecturalRequestsTab;
